import { inject, Injectable } from "@angular/core";
import { Action, Store } from "@ngrx/store";
import { filter, fromEvent, Observable } from "rxjs";
import { areActionsEqual } from "./helper/store.helper.service";
import { authActions } from "../store/auth/auth.actions";
import { Router } from "@angular/router";



@Injectable({
    providedIn: 'root'
})
export class PersistenceService {
    private store = inject(Store);
    private router = inject(Router);

    private busKey = "__bus";
    private openAntragsnummerListKey = "__openAntragsnummernList";

    // Observable for storage events
    storageChanges$: Observable<StorageEvent> = fromEvent<StorageEvent>(window, 'storage').pipe(
        // Only handle local storage events with the __bus key (this will hold all actions that should be dispatched)
        filter(event => event.storageArea === localStorage && event.key === this.busKey)
    );

    init(shouldBeLoggedOut: boolean): void {
        this.initPersistenceEventListener(shouldBeLoggedOut);
    }

    writeToBus(action: Action, overWriteAll = false): void {
        let activeBusArray: Action[] = [];
        if (!overWriteAll) {
            activeBusArray = this.getLocalStorageArray<Action>(this.busKey);
            //Check if the action was already in the bus then remove it
            if (activeBusArray.length > 0) {
                const inBusPosition = activeBusArray.findIndex((storedAction: Action) => storedAction.type === action.type);
                if (inBusPosition >= 0) {
                    activeBusArray.splice(inBusPosition, 1);
                }
            }
        }
        //Set the new action to the end and write it to the store.
        activeBusArray.push(action);
        localStorage.setItem(this.busKey, JSON.stringify(activeBusArray));
    }

    removeFromBus<T extends Action>(type: T['type'], propKey: string | null = null, propValue: unknown = null): void {
        const activeBusArray = this.getLocalStorageArray<Action>(this.busKey);
        if (activeBusArray.length > 0) {
            const inBusPosition = activeBusArray.findIndex((action: Action) => {
                const actionTypeMatch = action.type === type;
                if (propKey && actionTypeMatch) {
                    const specificAction = action as T;
                    const specificValue = specificAction[propKey as keyof T] === propValue;
                    return specificValue;
                }
                return actionTypeMatch;

            });
            if (inBusPosition >= 0) {
                activeBusArray.splice(inBusPosition, 1);
                localStorage.setItem(this.busKey, JSON.stringify(activeBusArray));
            }
        }
    }

    /**
     * Checks if a Antragsnummer is already open
     * @param antragsnummer the Antragsnummer that should be checked
     * @returns true if the Antragsnummer is not in the list and false if it is in the list.
     */
    canAntragsnummerBeOpened(antragsnummer: string, readonly = false): boolean {
        const activeAntragsnummerArray = this.getLocalStorageArray<string>(this.openAntragsnummerListKey);
        if (activeAntragsnummerArray.includes(antragsnummer)) {
            return false;
        }
        if (!readonly) {
            activeAntragsnummerArray.push(antragsnummer);
            localStorage.setItem(this.openAntragsnummerListKey, JSON.stringify(activeAntragsnummerArray));
        }
        return true;
    }

    /**
     * Removes a given Antragsnummer from the list of open Antragsnummern
     * @param antragsnummer the Antragsnummer that should be removed
     */
    removeAntragsnummerFromList(antragsnummer: string | null): void {
        if (antragsnummer === null) {
            localStorage.setItem(this.openAntragsnummerListKey, '[]');
            return;
        }
        const activeAntragsnummerArray = this.getLocalStorageArray<string>(this.openAntragsnummerListKey);
        if (activeAntragsnummerArray.length > 0) {
            const antragsnummerPosition = activeAntragsnummerArray.findIndex(antragsNr => antragsNr === antragsnummer);
            if (antragsnummerPosition >= 0) {
                activeAntragsnummerArray.splice(antragsnummerPosition, 1);
                localStorage.setItem(this.openAntragsnummerListKey, JSON.stringify(activeAntragsnummerArray));
            }
        }
    }

    private getLocalStorageArray<T>(storageKey: string): T[] {
        let activeStorage = localStorage.getItem(storageKey);
        //Check if the storageKey is set
        if (!activeStorage) {
            activeStorage = '[]'
        }
        //Check if the storageKey value is an array
        let activeStorageArray: T[];
        activeStorageArray = JSON.parse(activeStorage);
        if (!Array.isArray(activeStorageArray)) {
            activeStorageArray = [];
        }
        return activeStorageArray;
    }


    private handleStorageEvent(newValueString: string | null, oldValueString: string | null, shouldBeLoggedOut: boolean): void {
        //Check that the Values are Arrays
        let activeBusArray: Action[];
        let oldBusArray: Action[];
        try {
            activeBusArray = JSON.parse(newValueString ?? '');
            if (!Array.isArray(activeBusArray)) {
                activeBusArray = [];
            }
        } catch {
            activeBusArray = [];
        }
        try {
            oldBusArray = JSON.parse(oldValueString ?? '');
            if (!Array.isArray(oldBusArray)) {
                oldBusArray = [];
            }
        } catch {
            oldBusArray = [];
        }
        if (shouldBeLoggedOut && (activeBusArray.length === 0 || activeBusArray[0].type !== authActions.changeUserToLoggedOut.type)) {
            this.removeCognitoIdentityServiceProviderFromLocalStorage();
            this.store.dispatch(authActions.postChangeUserToLoggedOut({ timeout: true }));
            return;
        }
        activeBusArray.forEach((action: Action) => {
            if (oldBusArray.findIndex((oldStoredAction: Action) => areActionsEqual(oldStoredAction, action)) === -1) {
                this.store.dispatch(action);
            }
            if (action.type === authActions.changeUserToLoggedOut.type) {
                this.removeCognitoIdentityServiceProviderFromLocalStorage();
            }
        });

    }

    private initPersistenceEventListener(shouldBeLoggedOut: boolean): void {
        const startingStorage = localStorage.getItem(this.busKey);
        if (!startingStorage) {
            localStorage.setItem(this.busKey, '[]');
            this.removeCognitoIdentityServiceProviderFromLocalStorage();
            if (shouldBeLoggedOut) {
                this.store.dispatch(authActions.postChangeUserToLoggedOut({ timeout: true }));
            }
        } else {
            if (startingStorage === "[]") {
                this.removeCognitoIdentityServiceProviderFromLocalStorage();
                this.router.navigate(['/start']);
            }
            this.handleStorageEvent(startingStorage, null, shouldBeLoggedOut);
        }
        this.storageChanges$.subscribe(event => {
            this.handleStorageEvent(event.newValue, event.oldValue, false);
        });
    }

    private removeCognitoIdentityServiceProviderFromLocalStorage(): void {
        for (const key of Object.keys(localStorage)) {
            if (key.startsWith('CognitoIdentityServiceProvider')) {
                localStorage.removeItem(key);
            }
        }
    }
}